import React, { useState } from "react";
import "../../css/recentcourses.css";
import { useNavigate } from "react-router-dom";
import {
  prepareDashboardRecentCourseModule,
  useGetRecentCourses,
} from "../../Controllers/DashboardController";
import { AUTH_TOKEN } from "../../constants/localStorageKeys";
import { retrieve, save } from "../../helpers/StorageHelper";

function getActionButtonLabel(course) {
  if (course.all_courses_completed) return "Browse programs";
  const pct = Number(course.completion_percentage ?? 0);
  if (pct >= 100) return "Review";
  if (pct === 0) return "Start Here";
  return "Jump Back In";
}

const RecentCourseRowSkeleton = () => (
  <div className="recent-course-row recent-course-row-skeleton" aria-hidden>
    <div className="recent-course-thumb">
      <div
        className="box-skeleton-item"
        style={{ position: "absolute", inset: 0, borderRadius: 0 }}
      >
        <div className="box-skeleton-shimmer" />
      </div>
    </div>
    <div className="recent-course-meta">
      <div className="recent-course-progress-track">
        <div
          className="box-skeleton-item"
          style={{ width: "92px", height: "12px", borderRadius: "4px" }}
        >
          <div className="box-skeleton-shimmer" />
        </div>
        <div className="recent-course-progress-line" />
      </div>
      <div className="recent-course-content">
        <div
          className="box-skeleton-item"
          style={{
            width: "85%",
            height: "12px",
            borderRadius: "4px",
            marginBottom: "8px",
          }}
        >
          <div className="box-skeleton-shimmer" />
        </div>
        <div
          className="box-skeleton-item"
          style={{ width: "60%", height: "12px", borderRadius: "4px" }}
        >
          <div className="box-skeleton-shimmer" />
        </div>
        <div
          className="box-skeleton-item"
          style={{
            width: "100px",
            height: "32px",
            borderRadius: "999px",
            alignSelf: "flex-end",
            marginTop: "auto",
          }}
        >
          <div className="box-skeleton-shimmer" />
        </div>
      </div>
    </div>
  </div>
);

const RecentCourseRow = ({
  thumbnail,
  title,
  completion_percentage,
  course,
  onAction,
  actionBusy,
}) => {
  const label = getActionButtonLabel(course);

  return (
    <div className="recent-course-row">
      <div className="recent-course-thumb">
        <img src={thumbnail} alt="" />
      </div>
      <div className="recent-course-meta">
        <div className="recent-course-progress-track">
          <span className="recent-course-percent">
            {completion_percentage}% Complete
          </span>
          <div className="recent-course-progress-line">
            <div
              className="recent-course-progress-fill"
              style={{ width: `${completion_percentage}%` }}
            />
          </div>
        </div>
        <div className="recent-course-content">
          <p className="recent-course-title">{title}</p>
          <button
            type="button"
            className="recent-course-btn"
            disabled={actionBusy}
            aria-busy={actionBusy}
            onClick={onAction}
            style={{
              fontSize: "1em",
              padding: "18px 32px",
              minHeight: "48px",
              lineHeight: "1.25",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxSizing: "border-box",
            }}
          >
            {actionBusy ? (
              <span className="recent-course-btn-loading">
                <span className="recent-course-btn-spinner" aria-hidden />
                Loading…
              </span>
            ) : (
              label
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const RecentCoursesSection = () => {
  const navigate = useNavigate();
  const { data, isLoading: loading } = useGetRecentCourses();
  const courses = data?.recent_courses ?? [];
  const allCoursesCompleted = data?.all_courses_completed ?? false;
  const [busyCourseId, setBusyCourseId] = useState(null);

  /**
   * Same flow as CourseModuleView (open module) + CoursesView (chapterId + autoplay in location state).
   */
  const openCourse = async (course) => {
    if (course.all_courses_completed) {
      navigate("/courses");
      return;
    }

    const status = course.status;
    const courseId = course.current?.course_id ?? course.id;
    const moduleId = course.current?.module_id;
    const chapterId = course.current?.chapter_id;
    const token = retrieve(AUTH_TOKEN);

    if (status === "completed") {
      save("courseId", String(courseId));
      navigate(`/courses/${courseId}/modules`);
      return;
    }

    if (!token) return;

    setBusyCourseId(course.id);
    try {
      save("courseId", String(courseId));

      if (moduleId) {
        await prepareDashboardRecentCourseModule(moduleId, chapterId);

        navigate(`/modules/${moduleId}/chapters`, {
          state: {
            ...(chapterId != null ? { chapterId } : {}),
            autoPlayVideo: true,
          },
        });
      } else {
        navigate(`/courses/${courseId}/modules`);
      }
    } catch (error) {
      console.error("Error opening course from dashboard:", error);
    } finally {
      setBusyCourseId(null);
    }
  };

  return (
    <div className="recent-courses-card">
      <h3 className="recent-courses-heading">Recent Courses</h3>
      <div className="d-flex flex-column gap-3 h-100">
        {loading ? (
          <div aria-live="polite" aria-busy="true">
            <RecentCourseRowSkeleton />
          </div>
        ) : allCoursesCompleted ? (
          <div className="m-auto d-flex flex-column gap-3 text-center">
            <p
              className="text-body-b3 mb-0 recent-courses-empty"
              style={{ color: "var(--Text-200, #c8ced3)" }}
            >
              You have completed all the courses.
            </p>
            <img
              alt="image-alt"
              className="sidebar-logo"
              src="/icons/logo-horizontal.svg"
              style={{ width: "auto" }}
            />
          </div>
        ) : courses.length === 0 ? (
          <div className="m-auto d-flex flex-column gap-3">
            <p
              className="text-body-b3 mb-0 recent-courses-empty"
              style={{ color: "var(--Text-200, #c8ced3)" }}
            >
              Courses assigned to your tier will appear here once started.
            </p>
            <img
              alt="image-alt"
              className="sidebar-logo"
              src="/icons/logo-horizontal.svg"
              style={{ width: "auto" }}
            />
          </div>
        ) : (
          courses.map((course) => (
            <RecentCourseRow
              key={course.id}
              course={course}
              thumbnail={course.thumbnail}
              title={course.title}
              completion_percentage={course.completion_percentage ?? 0}
              onAction={() => openCourse(course)}
              actionBusy={busyCourseId === course.id}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default RecentCoursesSection;
